import { SHIP_GRID, WORLD_GEN } from "../constants";
import { v2, type Vec2 } from "../math";
import { T, derelictShipGrid, isBuilt, isGround, makeGrid, openToOutside, type Grid, type TileId } from "../tiles";
import { growFlora, plantLook, plantTile, type Plant } from "./plants";
import { Rng, fbm2, valueNoise2 } from "./rng";
import { asteroidPulls, type AsteroidDef, type Biome, type VineDef } from "./types";
import { mineralZone } from "./environment";

/**
 * An asteroid's tiles, made in passes (see lifeasteroids.md, "Rock anatomy"),
 * each a pure function of the def: shape, body, veins, caves, skin, flora,
 * features. Living rocks have their own (see livingRock). Player edits go on
 * top of this.
 */

/** Half the side length of an asteroid's square grid: room for its rock, and padding to build out into (and for a living rock's branches). */
export function asteroidHalf(radius: number, kind: AsteroidDef["kind"] = "rock", reach = 0, biome?: Biome): number {
  const extra = kind === "home" ? WORLD_GEN.homeExtraPad : biome === "grove" ? 12 : 0;
  return Math.ceil(radius * 1.25) + WORLD_GEN.asteroidPad + extra + reach;
}

/** How far a living rock of `radius` reaches with its branches. */
export const branchReach = (radius: number): number => Math.round(radius * WORLD_GEN.living.reach);

// ------------------------------------------------------------------ shape

export type ShapeFamily = "round" | "long" | "twin" | "cracked" | "shattered" | "vined";

/** One lump of rock: centred `at` (tiles from the grid's middle), `r` across, stretched `stretch` times along `angle`. */
interface Lobe {
  at: Vec2;
  r: number;
  stretch: number;
  angle: number;
  seed: number;
}

interface Shape {
  family: ShapeFamily;
  lobes: Lobe[];
  /** A canyon cut in from one side, along `angle`. */
  crack?: { angle: number; seed: number };
}

/** What shape an asteroid is: a vined rock in lobes; water, living rocks and rocks that pull (you walk all round those) round; the rest pick a family from their seed (WORLD_GEN.shapes). */
export function shapeFamily(def: AsteroidDef): ShapeFamily {
  if (def.vined) return "vined";
  if (def.pulls) return "round";
  if (def.kind !== "rock" && def.kind !== "metal" && def.kind !== "ice" && def.kind !== "dead" && def.kind !== "coal" && def.kind !== "iron") return "round";
  let roll = new Rng(def.seed ^ 0x5a7e).next();
  for (const [family, share] of Object.entries(WORLD_GEN.shapes)) {
    if (roll < share) return family as ShapeFamily;
    roll -= share;
  }
  return "round";
}

function shapeOf(def: AsteroidDef): Shape {
  const R = def.radius;
  const rng = new Rng(def.seed ^ 0x51a9);
  const angle = rng.range(0, Math.PI * 2);
  const lobe = (at: Vec2, r: number, stretch = 1, a = angle): Lobe => ({ at, r, stretch, angle: a, seed: rng.int(1, 0x7fffffff) });
  const dir = v2(Math.cos(angle), Math.sin(angle));
  const family = shapeFamily(def);
  switch (family) {
    case "long":
      return { family, lobes: [lobe(v2(), R * 0.72, 1.35)] };
    case "twin":
      return { family, lobes: [lobe(v2(dir.x * R * 0.45, dir.y * R * 0.45), R * 0.62), lobe(v2(-dir.x * R * 0.45, -dir.y * R * 0.45), R * 0.5)] };
    case "cracked":
      return { family, lobes: [lobe(v2(), R)], crack: { angle, seed: rng.int(1, 0x7fffffff) } };
    case "vined": {
      // Lobes round the middle, each wholly inside the rock's widest outline (a lobe's reaches 1.25 its r). Sized so neighbours,
      // as far round as they can be jittered toward each other, leave `gap` between at their widest: 2.5 (R - r) sin(a/2) >= 2.5 r + gap, `a` the least angle between them.
      const V = WORLD_GEN.vined;
      const n = rng.int(V.lobes[0], V.lobes[1]);
      const jitter = 0.12;
      const s = Math.sin(Math.PI / n - jitter);
      const r = (2.5 * R * s - V.gap) / (2.5 * (1 + s));
      const d = 1.25 * (R - r);
      const lobes: Lobe[] = [];
      for (let i = 0; i < n; i++) {
        const a = angle + (i / n) * Math.PI * 2 + rng.range(-jitter, jitter);
        lobes.push(lobe(v2(Math.cos(a) * d, Math.sin(a) * d), r * rng.range(0.85, 1), 1, a));
      }
      return { family, lobes };
    }
    case "shattered": {
      const n = rng.int(3, 5);
      const lobes: Lobe[] = [];
      for (let i = 0; i < n; i++) {
        const a = angle + (i / n) * Math.PI * 2 + rng.range(-0.3, 0.3);
        const d = R * rng.range(0.45, 0.6);
        lobes.push(lobe(v2(Math.cos(a) * d, Math.sin(a) * d), R * rng.range(0.32, 0.45), 1, a));
      }
      return { family, lobes };
    }
    default:
      return { family, lobes: [lobe(v2(), R)] };
  }
}

/** A lobe's surface at angle `ang` from its middle (noise sampled on a circle so it wraps without a seam; low frequency, so the surface is smooth). */
function lobeEdge(l: Lobe, ang: number): number {
  return l.r * (0.8 + 0.45 * fbm2(l.seed, Math.cos(ang) * 1.1 + 50, Math.sin(ang) * 1.1 + 50));
}

/** How deep (dx, dy) (tiles from the grid's middle) is inside one lobe: positive inside, the distance to its surface. */
function lobeDepth(l: Lobe, dx: number, dy: number): number {
  let x = dx - l.at.x;
  let y = dy - l.at.y;
  if (l.stretch !== 1) {
    const c = Math.cos(-l.angle);
    const s = Math.sin(-l.angle);
    [x, y] = [(x * c - y * s) / l.stretch, x * s + y * c];
  }
  return lobeEdge(l, Math.atan2(y, x)) - Math.hypot(x, y);
}

/**
 * How deep a point is in the rock: positive inside (about the distance to the
 * outer surface), negative outside. Shattered rocks have gaps where their
 * chunks meet; a cracked one, a canyon from one side to past its middle.
 */
function depthAt(shape: Shape, def: AsteroidDef, dx: number, dy: number): number {
  let best = -Infinity;
  let second = -Infinity;
  for (const l of shape.lobes) {
    const d = lobeDepth(l, dx, dy);
    if (d > best) [best, second] = [d, best];
    else if (d > second) second = d;
  }
  if (shape.family === "shattered" && second > -1 && best - second < 1.6) return -1;
  if (shape.crack) {
    const c = Math.cos(shape.crack.angle);
    const s = Math.sin(shape.crack.angle);
    const along = dx * c + dy * s;
    const across = -dx * s + dy * c;
    // From the far side in to a little past the middle, narrowing as it goes.
    if (along < def.radius * 0.2) {
      const t = (def.radius * 0.2 - along) / (def.radius * 1.4);
      const half = (1.2 + 3 * Math.min(1, t)) * (0.7 + 0.6 * valueNoise2(shape.crack.seed, along * 0.12, 0.5));
      if (Math.abs(across + (valueNoise2(shape.crack.seed + 1, along * 0.06, 0.5) - 0.5) * 6) < half) return -1;
    }
  }
  return best;
}

// --------------------------------------------------------------- the grid

/** Is (x, y) inside a grid of side w x h? */
const inside = (g: Grid, x: number, y: number) => x >= 0 && y >= 0 && x < g.w && y < g.h;

/**
 * The untouched tiles of an asteroid, surrounded by empty padding. Player
 * edits are applied on top.
 * - rock (and its kinds): a shape, layered body, veins, caves, its biome's skin and plants.
 * - home: round, grassed all over, with tall trees and void crystals.
 * - water: a wobbly ball of water.
 * - living: stone in a bark shell with branches (see livingRock).
 */
export function generateAsteroidGrid(def: AsteroidDef): Grid {
  const g = makeGrid(def.w, def.h);
  // On a rock that pulls, up is the grid direction nearest to out from the middle: pieces and art turn to it (see turnAt).
  if (asteroidPulls(def)) g.turns = true;
  let plants: Plant[] = [];
  if (def.kind === "living") livingRock(g, def);
  else {
    const depth = shapePass(g, def);
    if (def.kind !== "water") {
      bodyPass(g, def, depth);
      veinPass(g, def, depth);
      cavePass(g, def, depth);
      smoothPass(g);
      if (def.vined) gripPass(g, def);
      const biome = def.biome ?? (def.kind === "home" ? "meadow" : "dark");
      skinPass(g, def, biome);
      plants = growFlora(g, def, biome);
      featurePass(g, def, biome);
    }
  }
  plantsOf.set(plantKey(def), indexPlants(plants));
  return g;
}

/** Solid where the shape is: rock, or water for the water ball. Returns each tile's depth (see depthAt). */
function shapePass(g: Grid, def: AsteroidDef): Float32Array {
  const shape = shapeOf(def);
  const depth = new Float32Array(g.w * g.h);
  const cx = g.w / 2;
  const cy = g.h / 2;
  for (let y = 0; y < g.h; y++) {
    for (let x = 0; x < g.w; x++) {
      const d = depthAt(shape, def, x + 0.5 - cx, y + 0.5 - cy);
      depth[y * g.w + x] = d;
      if (d > 0) g.tiles[y * g.w + x] = def.kind === "water" ? T.WATER : T.ROCK;
    }
  }
  return depth;
}

/**
 * The layers, outside in: loose regolith, stone, a dense core. Ice rocks have
 * ice through them; a hollow rock is a shell round one big cave; a nugget is
 * coal or ore right through (a few flecks of stone in it), bare to the sky.
 */
function bodyPass(g: Grid, def: AsteroidDef, depth: Float32Array): void {
  if (isNugget(def)) {
    const fill = def.kind === "coal" ? T.COAL : T.ORE;
    for (let i = 0; i < g.tiles.length; i++) {
      if (g.tiles[i] !== T.ROCK) continue;
      if (valueNoise2(def.seed + 43, (i % g.w) * 0.3, Math.floor(i / g.w) * 0.3) < 0.88) g.tiles[i] = fill;
    }
    return;
  }
  const regolith = 2 + Math.floor(valueNoise2(def.seed + 3, 0.5, 0.5) * 3);
  const core = def.radius * (def.kind === "metal" ? 0.4 : 0.5);
  for (let i = 0; i < g.tiles.length; i++) {
    if (g.tiles[i] !== T.ROCK) continue;
    const d = depth[i]!;
    const x = i % g.w;
    const y = Math.floor(i / g.w);
    if (def.kind === "hollow" && d > def.radius * 0.38) g.tiles[i] = T.EMPTY;
    else if (def.kind === "ice" && valueNoise2(def.seed + 41, x * 0.08, y * 0.08) > 0.38) g.tiles[i] = T.ICE;
    // Wet rocks have pockets of clay in and under their loose outer layer; flares have melted patches of a scorched rock's core to obsidian.
    else if (d < regolith + 5 && def.kind !== "home" && def.kind !== "ice" && def.biome !== "frost" && def.biome !== "scorched" && (def.wet ?? 0) >= 0.5 && valueNoise2(def.seed + 47, x * 0.12, y * 0.12) > 0.55) g.tiles[i] = T.CLAY;
    else if (d < regolith && def.kind !== "home") g.tiles[i] = T.REGOLITH;
    else if (d > core && def.kind !== "home") g.tiles[i] = def.biome === "scorched" && valueNoise2(def.seed + 49, x * 0.1, y * 0.1) > 0.5 ? T.OBSIDIAN : T.DEEP_ROCK;
  }
}

/**
 * Is (x, y) on a vein: a seam about two tiles wide along a contour of noise
 * at frequency `f`, only where a broader noise lets it run (more of the
 * time, the higher `rich`), so ore follows seams worth chasing rather than
 * sitting in blobs.
 */
const vein = (seed: number, x: number, y: number, f: number, rich: number) =>
  Math.abs(valueNoise2(seed, x * f, y * f) - 0.5) < f && valueNoise2(seed + 1, x * f * 0.4, y * f * 0.4) > 1 - rich;

/** A nugget (see WORLD_GEN.minerals.nuggets): almost all coal or iron ore. */
const isNugget = (def: AsteroidDef) => def.kind === "coal" || def.kind === "iron";

/**
 * Ore and coal as veins along noise ridges, more or less of each by the
 * rock's mineral zone and the wealth of its system (see WORLD_GEN.minerals);
 * scorched rocks get ember ore near the surface.
 */
function veinPass(g: Grid, def: AsteroidDef, depth: Float32Array): void {
  if (isNugget(def)) return;
  const zone = WORLD_GEN.minerals.zones[mineralZone(def.heat ?? 0)];
  const wealth = def.wealth ?? 1;
  const ore = Math.min(0.9, (def.kind === "metal" ? 0.75 : 0.35) * zone.ore * wealth);
  const coal = Math.min(0.9, (def.kind === "metal" ? 0.2 : 0.4) * zone.coal * wealth);
  const scorched = def.biome === "scorched";
  for (let i = 0; i < g.tiles.length; i++) {
    const t = g.tiles[i]!;
    if (t !== T.ROCK && t !== T.DEEP_ROCK && t !== T.REGOLITH) continue;
    const x = i % g.w;
    const y = Math.floor(i / g.w);
    if (t !== T.REGOLITH && vein(def.seed + 13, x, y, 0.05, ore)) g.tiles[i] = T.ORE;
    else if (vein(def.seed + 29, x, y, 0.035, coal)) g.tiles[i] = T.COAL;
    else if (scorched && depth[i]! < 8 && vein(def.seed + 37, x, y, 0.07, 0.5)) g.tiles[i] = T.EMBER_ORE;
  }
}

export type CaveStyle = "chambers" | "tunnels" | "both" | "geode";

/**
 * How a rock's caves run: chambers from noise, worm tunnels, both, or a
 * hollow heart lined with crystal (big enough rocks only, and most often out
 * in the cold: see WORLD_GEN.minerals).
 */
export function caveStyle(def: AsteroidDef): CaveStyle {
  if (def.kind === "home") return "chambers";
  const roll = new Rng(def.seed ^ 0xca5e).next();
  const zone = mineralZone(def.heat ?? 0);
  const geodes = WORLD_GEN.minerals.zones[zone].geodes;
  if (roll < geodes && def.radius >= (zone === "cold" ? 20 : 30)) return "geode";
  // The rest as they always were, whatever share went to geodes.
  const r = Math.max(0, roll - geodes) / (1 - geodes);
  if (r < 0.38) return "chambers";
  if (r < 0.78) return "tunnels";
  return "both";
}

function cavePass(g: Grid, def: AsteroidDef, depth: Float32Array): void {
  // A nugget is solid through.
  if (isNugget(def)) return;
  const style = caveStyle(def);
  const rng = new Rng(def.seed ^ 0x7a11);
  const dig = (x: number, y: number) => {
    if (inside(g, x, y)) g.tiles[y * g.w + x] = T.EMPTY;
  };
  if (style === "chambers" || style === "both") {
    for (let i = 0; i < g.tiles.length; i++) {
      if (!g.tiles[i] || depth[i]! < 6) continue;
      if (fbm2(def.seed + 7, (i % g.w) * 0.04, Math.floor(i / g.w) * 0.04) > 0.64) g.tiles[i] = T.EMPTY;
    }
  }
  if (style === "tunnels" || style === "both") {
    const worms = rng.int(1, 2 + Math.floor(def.radius / 25));
    for (let w = 0; w < worms; w++) {
      // Most start inside; some at the surface, so the way in is there to find.
      const open = rng.next() < 0.3;
      const a = rng.range(0, Math.PI * 2);
      const r = def.radius * (open ? 0.95 : rng.range(0.1, 0.6));
      let p = v2(g.w / 2 + Math.cos(a) * r, g.h / 2 + Math.sin(a) * r);
      let heading = open ? a + Math.PI : rng.range(0, Math.PI * 2);
      const len = def.radius * rng.range(1.2, 2.6);
      const width = rng.range(1.6, 2.4);
      for (let s = 0; s < len; s += 0.5) {
        heading += (valueNoise2(def.seed + 91 + w, s * 0.08, 0.5) - 0.5) * 0.5;
        p = v2(p.x + Math.cos(heading) * 0.5, p.y + Math.sin(heading) * 0.5);
        const i = Math.floor(p.y) * g.w + Math.floor(p.x);
        // Turn back in before breaking out the far side.
        if (!inside(g, Math.floor(p.x), Math.floor(p.y)) || (depth[i]! < 3 && s > 8)) heading += Math.PI * 0.6;
        for (let oy = -width; oy <= width; oy++) for (let ox = -width; ox <= width; ox++) if (ox * ox + oy * oy <= width * width) dig(Math.floor(p.x + ox), Math.floor(p.y + oy));
      }
    }
  }
  if (style === "geode") {
    const r = def.radius * 0.3;
    const cx = g.w / 2;
    const cy = g.h / 2;
    for (let y = Math.floor(cy - r - 2); y <= cy + r + 2; y++) {
      for (let x = Math.floor(cx - r - 2); x <= cx + r + 2; x++) {
        const d = Math.hypot(x + 0.5 - cx, y + 0.5 - cy) - r * (0.9 + 0.2 * valueNoise2(def.seed + 5, x * 0.2, y * 0.2));
        if (d < 0) dig(x, y);
      }
    }
    // Lined with crystal: shards along the wall, pointing in.
    const crystal = plantTile("crystals", plantLook(def));
    for (let y = Math.floor(cy - r - 2); y <= cy + r + 2; y++) {
      for (let x = Math.floor(cx - r - 2); x <= cx + r + 2; x++) {
        if (!inside(g, x, y) || g.tiles[y * g.w + x] !== T.EMPTY) continue;
        const wall = [[1, 0], [-1, 0], [0, 1], [0, -1]].some(([dx, dy]) => isGround(g.tiles[(y + dy!) * g.w + x + dx!] ?? 0));
        if (wall && valueNoise2(def.seed + 17, x * 0.5, y * 0.5) > 0.35) g.tiles[y * g.w + x] = crystal;
      }
    }
  }
}

/** Where a vined rock's vines grip (see rockVines): rock filled in from each end back to its lobe, so a cave or the smoothing never leaves one hanging in the air. */
function gripPass(g: Grid, def: AsteroidDef): void {
  const { lobes } = shapeOf(def);
  const dist = (l: Lobe, p: Vec2) => Math.hypot(l.at.x - p.x, l.at.y - p.y);
  for (const v of rockVines(def)) {
    for (const p of [v.a, v.b]) {
      const l = lobes.reduce((m, x) => (dist(x, p) < dist(m, p) ? x : m));
      const len = dist(l, p);
      for (let s = 0; s <= len; s += 0.5) {
        const x = Math.floor(g.w / 2 + p.x + ((l.at.x - p.x) * s) / len);
        const y = Math.floor(g.h / 2 + p.y + ((l.at.y - p.y) * s) / len);
        if (g.tiles[y * g.w + x] !== T.EMPTY) break;
        for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) if (inside(g, x + dx, y + dy) && g.tiles[(y + dy) * g.w + x + dx] === T.EMPTY) g.tiles[(y + dy) * g.w + x + dx] = T.ROCK;
      }
    }
  }
}

/** Rid the ground of one-tile bumps and notches (twice), so slopes and curves read smooth (see lifeasteroids.md, "Smooth ground"). */
function smoothPass(g: Grid): void {
  for (let pass = 0; pass < 2; pass++) {
    const was = g.tiles.slice();
    for (let y = 1; y < g.h - 1; y++) {
      for (let x = 1; x < g.w - 1; x++) {
        const i = y * g.w + x;
        const t = was[i]!;
        const solid = [was[i - 1]!, was[i + 1]!, was[i - g.w]!, was[i + g.w]!].filter((n) => isGround(n));
        if (isGround(t) && solid.length <= 1) g.tiles[i] = T.EMPTY;
        else if (t === T.EMPTY && solid.length >= 3) g.tiles[i] = solid[0]!;
      }
    }
  }
}

/** What covers a rock's surface in each biome, and how deep. */
const SKIN: Partial<Record<Biome, { tile: TileId; depth: number }>> = {
  meadow: { tile: T.GRASS, depth: 2 },
  grove: { tile: T.GRASS, depth: 3 },
  wreck: { tile: T.GRASS, depth: 1 },
  twilight: { tile: T.MOSS, depth: 2 },
  lichen: { tile: T.LICHEN, depth: 1 },
  frost: { tile: T.FROST, depth: 2 },
  scorched: { tile: T.ASH, depth: 2 },
  dark: { tile: T.MYCELIUM, depth: 2 },
  barren: { tile: T.SALT, depth: 1 },
};

/**
 * How far each solid tile is from open space connected to the outside, in
 * steps through rock (1 for the surface), up to `max`; 0 for open tiles and
 * anything deeper.
 */
export function surfaceDepth(g: Grid, max: number): Uint8Array {
  const out = openToOutside(g);
  const depth = new Uint8Array(g.w * g.h);
  let frontier: number[] = [];
  for (let i = 0; i < g.tiles.length; i++) {
    if (!out[i]) continue;
    const x = i % g.w;
    for (const n of [x > 0 ? i - 1 : -1, x < g.w - 1 ? i + 1 : -1, i - g.w, i + g.w]) {
      if (n >= 0 && n < g.tiles.length && isGround(g.tiles[n]!) && !depth[n]) {
        depth[n] = 1;
        frontier.push(n);
      }
    }
  }
  for (let d = 2; d <= max && frontier.length; d++) {
    const next: number[] = [];
    for (const i of frontier) {
      const x = i % g.w;
      for (const n of [x > 0 ? i - 1 : -1, x < g.w - 1 ? i + 1 : -1, i - g.w, i + g.w]) {
        if (n >= 0 && n < g.tiles.length && isGround(g.tiles[n]!) && !depth[n]) {
          depth[n] = d;
          next.push(n);
        }
      }
    }
    frontier = next;
  }
  return depth;
}

/**
 * The biome's ground cover over the outer surface. In poor light it comes
 * in patches, fewer the less light (as grass always did); frost, ash,
 * mycelium (on the dark rocks) and salt (on the barren ones) cover whatever
 * the light. Scorched rocks have glassy patches too, and dry meadows sandy ones.
 */
function skinPass(g: Grid, def: AsteroidDef, biome: Biome): void {
  const skin = SKIN[biome];
  // A nugget is ore or coal bare to the sky.
  if (!skin || isNugget(def)) return;
  const light = def.kind === "home" ? 1 : (def.light ?? 0);
  const patchy = biome !== "frost" && biome !== "scorched" && biome !== "dark" && biome !== "barren";
  const sandy = biome === "meadow" && (def.wet ?? 0) < 0.3;
  if (patchy && light < WORLD_GEN.life.grass) return;
  const depth = surfaceDepth(g, skin.depth + (def.kind === "home" ? 0 : 1));
  const cx = g.w / 2;
  const cy = g.h / 2;
  for (let i = 0; i < g.tiles.length; i++) {
    const d = depth[i]!;
    if (!d) continue;
    const x = i % g.w;
    const y = Math.floor(i / g.w);
    const a = Math.atan2(y + 0.5 - cy, x + 0.5 - cx);
    if (patchy && light < 1 && valueNoise2(def.seed + 53, Math.cos(a) * 3 + 50, Math.sin(a) * 3 + 50) >= light) continue;
    const deep = patchy && light <= 0.6 ? 1 : skin.depth;
    if (d > deep) continue;
    if (biome === "scorched" && valueNoise2(def.seed + 59, x * 0.15, y * 0.15) > 0.6) g.tiles[i] = T.SUNGLASS;
    else if (sandy && valueNoise2(def.seed + 61, x * 0.12, y * 0.12) > 0.62) g.tiles[i] = T.SAND;
    else g.tiles[i] = skin.tile;
  }
}

/** One-off things: a buried wreck (dead rocks), a spring, ruins. */
function featurePass(g: Grid, def: AsteroidDef, biome: Biome): void {
  const rng = new Rng(def.seed ^ 0xfea7);
  if (def.kind === "dead") buryWreck(g, def);
  // A spring: a pocket of water in a cave, where it's wet.
  if ((def.wet ?? 0) >= 0.5 && def.kind !== "home" && !isNugget(def) && rng.next() < 0.4) spring(g, def, rng);
  // Ruins: a little old frame half sunk in the ground, on rocks out in the dimmer light.
  if ((biome === "twilight" || biome === "lichen") && rng.next() < 0.12) ruins(g, def, rng);
}

/** A dead rock: a cave in its heart holding an old wreck (timber, iron, a furnace and engines worth digging out). */
function buryWreck(g: Grid, def: AsteroidDef): void {
  const cx = Math.floor(g.w / 2);
  const cy = Math.floor(g.h / 2);
  const r = Math.min(16, def.radius * 0.5);
  for (let y = 0; y < g.h; y++) for (let x = 0; x < g.w; x++) if (Math.hypot(x + 0.5 - cx, (y + 0.5 - cy) * 1.4) < r) g.tiles[y * g.w + x] = T.EMPTY;
  const wreck = derelictShipGrid(SHIP_GRID.w, SHIP_GRID.h);
  const rng = new Rng(def.seed ^ 0xdead);
  // Bits have rotted away.
  for (let i = 0; i < wreck.tiles.length; i++) {
    const t = wreck.tiles[i]!;
    if (!t) continue;
    const x = (i % wreck.w) - Math.floor(wreck.w / 2) + cx;
    const y = Math.floor(i / wreck.w) - Math.floor(wreck.h / 2) + cy - 2;
    if (x < 0 || y < 0 || x >= g.w || y >= g.h) continue;
    if ((isBuilt(t, "plank") || isBuilt(t, "wall")) && rng.next() < 0.25) continue;
    g.tiles[y * g.w + x] = t;
  }
  // Nothing of it can be seen until you dig in: the way in is through the rock.
}

/** Fill the lowest part (toward the middle) of a cave pocket with water. */
function spring(g: Grid, def: AsteroidDef, rng: Rng): void {
  const out = openToOutside(g);
  const cx = g.w / 2;
  const cy = g.h / 2;
  for (let tries = 0; tries < 40; tries++) {
    const x = rng.int(0, g.w - 1);
    const y = rng.int(0, g.h - 1);
    const i = y * g.w + x;
    if (g.tiles[i] !== T.EMPTY || out[i] || Math.hypot(x - cx, y - cy) > def.radius) continue;
    // Water settles toward the middle: fill a small disc pulled that way.
    const a = Math.atan2(cy - y, cx - x);
    const c = v2(x + Math.cos(a) * 2, y + Math.sin(a) * 2);
    for (let dy = -3; dy <= 3; dy++) {
      for (let dx = -3; dx <= 3; dx++) {
        const px = Math.floor(c.x + dx);
        const py = Math.floor(c.y + dy);
        if (inside(g, px, py) && dx * dx + dy * dy <= 9 && g.tiles[py * g.w + px] === T.EMPTY && !out[py * g.w + px]) g.tiles[py * g.w + px] = T.WATER;
      }
    }
    return;
  }
}

/** An old frame of timber and iron, half sunk into the surface: someone lived here once. */
function ruins(g: Grid, def: AsteroidDef, rng: Rng): void {
  const a = rng.range(0, Math.PI * 2);
  const cx = g.w / 2;
  const cy = g.h / 2;
  // March in from outside to the surface.
  let d = def.radius * 1.3;
  while (d > 0) {
    const x = Math.floor(cx + Math.cos(a) * d);
    const y = Math.floor(cy + Math.sin(a) * d);
    if (inside(g, x, y) && isGround(g.tiles[y * g.w + x]!)) break;
    d -= 0.5;
  }
  if (d <= 0) return;
  const bx = Math.floor(cx + Math.cos(a) * (d - 2));
  const by = Math.floor(cy + Math.sin(a) * (d - 2));
  const w = rng.int(6, 10);
  const h = rng.int(4, 6);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const px = bx - (w >> 1) + x;
      const py = by - (h >> 1) + y;
      if (!inside(g, px, py)) continue;
      const edge = x === 0 || y === 0 || x === w - 1 || y === h - 1;
      // Rotted through in places.
      if (rng.next() < 0.3) continue;
      g.tiles[py * g.w + px] = edge ? (rng.next() < 0.2 ? T.SHIPWRIGHT_IRON : T.SHIPWRIGHT_PLANK) : T.SHIPWRIGHT_WALL;
    }
  }
}

// ------------------------------------------------------------ living rocks

/** A living rock's bark surface at angle `ang`: round, with creases running along it (its grain), not lumps. */
export function livingEdge(def: AsteroidDef, ang: number): number {
  const R = def.radius;
  const lump = 0.92 + 0.12 * fbm2(def.seed, Math.cos(ang) * 0.9 + 50, Math.sin(ang) * 0.9 + 50);
  const crease = 0.7 * Math.sin(ang * R * 0.45 + 4 * fbm2(def.seed + 3, Math.cos(ang) * 2 + 50, Math.sin(ang) * 2 + 50));
  return R * lump + crease;
}

/**
 * A vined rock's vines (see VineDef): each lobe tied to the next one round by
 * one or two, and now and then the last back to the first. Each end is two
 * blocks in from its lobe's surface, on the side facing the other lobe, give
 * or take (see gripPass).
 */
export function rockVines(def: AsteroidDef): VineDef[] {
  if (!def.vined) return [];
  const { lobes } = shapeOf(def);
  const rng = new Rng(def.seed ^ 0x7155);
  const vines: VineDef[] = [];
  const round3 = (x: number) => Math.round(x * 1000) / 1000;
  const end = (l: Lobe, ang: number) => {
    const d = lobeEdge(l, ang) - 2;
    return v2(round3(l.at.x + Math.cos(ang) * d), round3(l.at.y + Math.sin(ang) * d));
  };
  const tie = (p: Lobe, q: Lobe) => {
    const ang = Math.atan2(q.at.y - p.at.y, q.at.x - p.at.x);
    for (let k = 0, n = rng.int(1, 2); k < n; k++) {
      const spread = rng.range(-0.45, 0.45);
      const a = end(p, ang + spread);
      const b = end(q, ang + Math.PI - spread * rng.range(0.5, 1.5));
      const len = Math.hypot(b.x - a.x, b.y - a.y);
      vines.push({ id: `${def.id}v${vines.length}`, rock: def.id, a, b, sag: Math.max(1, Math.round(len * rng.range(0.08, 0.2))), thickness: round3(rng.range(0.8, 1.5)), seed: rng.int(1, 0x7fffffff) });
    }
  };
  for (let i = 1; i < lobes.length; i++) tie(lobes[i - 1]!, lobes[i]!);
  if (lobes.length > 2 && rng.next() < 0.5) tie(lobes[lobes.length - 1]!, lobes[0]!);
  return vines;
}

/** Does a living rock of this size have a heart? */
export const hasHeart = (def: AsteroidDef): boolean => def.kind === "living" && def.radius >= WORLD_GEN.living.heartRadius;

/** Where a living rock's heart is (its tile index), if it has one. */
export function heartIndex(def: AsteroidDef): number | null {
  return hasHeart(def) ? Math.floor(def.h / 2) * def.w + Math.floor(def.w / 2) : null;
}

/**
 * A living rock (see lifeasteroids.md, "Living rocks"), outside in: branches
 * toward the sun, a bark shell (4-8 tiles), a thin layer of sapwood with sap
 * pockets, and a core: heartwood round a heart in the big ones, stone with
 * ore in the small ones (the bark grew round an ordinary rock). Knot-holes in
 * the bark lead into tunnels between the bark and the core. A dead one (its
 * heart taken) is a husk: no foliage, its branches broken off to stubs.
 */
function livingRock(g: Grid, def: AsteroidDef): void {
  const rng = new Rng(def.seed ^ 0x11fe);
  const R = def.radius;
  const [b0, b1] = WORLD_GEN.living.bark;
  const bark = Math.round(b0 + (b1 - b0) * Math.min(1, Math.max(0, (R - 20) / 40)));
  const heart = hasHeart(def);
  const cx = g.w / 2;
  const cy = g.h / 2;
  const dead = !!def.dead;
  // Tunnels between bark and core, round part of the rock.
  const tunnelAt = (ang: number) => valueNoise2(def.seed + 23, Math.cos(ang) * 1.5 + 50, Math.sin(ang) * 1.5 + 50) > 0.55;
  for (let y = 0; y < g.h; y++) {
    for (let x = 0; x < g.w; x++) {
      const dx = x + 0.5 - cx;
      const dy = y + 0.5 - cy;
      const ang = Math.atan2(dy, dx);
      const depth = livingEdge(def, ang) - Math.hypot(dx, dy);
      if (depth <= 0) continue;
      const i = y * g.w + x;
      let t: TileId;
      if (depth < bark) t = T.BARK;
      else if (depth < bark + 2) t = valueNoise2(def.seed + 31, x * 0.3, y * 0.3) > 0.8 ? T.SAP : T.SAPWOOD;
      else if (depth < bark + 4 && tunnelAt(ang)) t = T.EMPTY;
      else if (heart) t = T.HEARTWOOD;
      else t = vein(def.seed + 13, x, y, 0.06, 0.5) ? T.ORE : depth > R * 0.5 ? T.DEEP_ROCK : T.ROCK;
      if (dead && (t === T.BARK || t === T.SAPWOOD || t === T.SAP)) t = T.HUSK;
      g.tiles[i] = t;
    }
  }
  const hi = heartIndex(def);
  if (hi !== null && !dead) g.tiles[hi] = T.HEART;
  else if (hi !== null) g.tiles[hi] = T.EMPTY;

  // Knot-holes: a few ways in through the bark to the tunnels.
  const holes = rng.int(1, 3);
  for (let k = 0; k < holes; k++) {
    let ang = rng.range(0, Math.PI * 2);
    for (let s = 0; s < 40 && !tunnelAt(ang); s++) ang += 0.15;
    const edge = livingEdge(def, ang);
    for (let d = edge + 1; d > edge - bark - 3; d -= 0.4) {
      for (const o of [-1.2, -0.4, 0.4, 1.2]) {
        const x = Math.floor(cx + Math.cos(ang) * d - Math.sin(ang) * o);
        const y = Math.floor(cy + Math.sin(ang) * d + Math.cos(ang) * o);
        if (inside(g, x, y)) g.tiles[y * g.w + x] = T.EMPTY;
      }
    }
  }

  branches(g, def, rng, dead);
}

/**
 * Thick limbs out of the bark, mostly sunward, forking as they go and
 * thinning, tipped with foliage. Solid wood you can walk along near the
 * trunk; foliage (not solid) at the ends. Grown only into empty space.
 */
function branches(g: Grid, def: AsteroidDef, rng: Rng, dead: boolean): void {
  const reach = def.reach ?? branchReach(def.radius);
  const sun = def.sunward ?? rng.range(0, Math.PI * 2);
  const heat = def.heat ?? WORLD_GEN.living.heat;
  const n = 3 + Math.round(((heat - WORLD_GEN.living.heat) / (1 - WORLD_GEN.living.heat)) * 4) + rng.int(0, 1);
  const cx = g.w / 2;
  const cy = g.h / 2;
  const put = (x: number, y: number, t: TileId, over: boolean) => {
    const xi = Math.floor(x);
    const yi = Math.floor(y);
    if (!inside(g, xi, yi)) return;
    const was = g.tiles[yi * g.w + xi]!;
    if (was === T.EMPTY || (over && was === T.FOLIAGE)) g.tiles[yi * g.w + xi] = t;
  };
  const limb = (from: Vec2, dir: number, len: number, width: number, depth: number, trunk: number) => {
    let p = from;
    let a = dir;
    for (let s = 0; s < len; s += 0.4) {
      // Bend a little toward the sun as it grows.
      a += Math.sin(sun - a) * 0.012 + (valueNoise2(def.seed + 101 + depth, s * 0.1, trunk) - 0.5) * 0.06;
      p = v2(p.x + Math.cos(a) * 0.4, p.y + Math.sin(a) * 0.4);
      const w = Math.max(1, width * (1 - (s / len) * 0.6));
      const fromTrunk = trunk + s;
      // A dead rock's branches have broken off, all but stubs.
      if (dead && fromTrunk > 3) return;
      for (let o = -(w - 1) / 2; o <= (w - 1) / 2 + 1e-9; o += 0.5) put(p.x - Math.sin(a) * o, p.y + Math.cos(a) * o, dead ? T.HUSK : T.BRANCH, true);
      if (depth < 2 && s > len * 0.45 && s < len * 0.45 + 0.4) {
        const fork = rng.range(0.35, 0.7) * (rng.next() < 0.5 ? 1 : -1);
        limb(p, a + fork, len * 0.55, Math.max(1, width * 0.6), depth + 1, fromTrunk);
      }
    }
    if (dead) return;
    // Foliage round the tip.
    const r = 2 + rng.range(0, 2) + (2 - depth) * 0.5;
    for (let dy = -r; dy <= r; dy += 0.5) for (let dx = -r; dx <= r; dx += 0.5) if (dx * dx + dy * dy <= r * r && valueNoise2(def.seed + 7, (p.x + dx) * 0.6, (p.y + dy) * 0.6) > 0.25) put(p.x + dx, p.y + dy, T.FOLIAGE, false);
  };
  for (let k = 0; k < n; k++) {
    // Most face the sun; a few anywhere.
    const ang = k < n - 1 ? sun + rng.range(-1.3, 1.3) : rng.range(0, Math.PI * 2);
    const edge = livingEdge(def, ang) - 1.5;
    const from = v2(cx + Math.cos(ang) * edge, cy + Math.sin(ang) * edge);
    limb(from, ang, reach * rng.range(0.55, 0.85), rng.range(3, 5), 0, 0);
  }
}

// ------------------------------------------------------------------ plants

interface AsteroidPlants {
  plants: Plant[];
  /** Tile index -> which plant it's part of. */
  byTile: Map<number, number>;
}

const plantsOf = new Map<string, AsteroidPlants>();
const plantKey = (def: AsteroidDef) => `${def.id}:${def.seed}:${def.dead ? 1 : 0}`;

function indexPlants(plants: Plant[]): AsteroidPlants {
  const byTile = new Map<number, number>();
  plants.forEach((p, n) => p.tiles.forEach((i) => byTile.set(i, n)));
  return { plants, byTile };
}

function plantsFor(def: AsteroidDef): AsteroidPlants {
  let p = plantsOf.get(plantKey(def));
  if (!p) {
    generateAsteroidGrid(def);
    p = plantsOf.get(plantKey(def))!;
  }
  return p;
}

/** A rock's been let go (nobody's near: see WAKE): forget its plants, made again with it when it's next built. */
export function forgetPlants(def: AsteroidDef): void {
  plantsOf.delete(plantKey(def));
}

/** Every plant an asteroid grew, in a fixed order (a plant's number is its place in it). */
export function asteroidPlants(def: AsteroidDef): readonly Plant[] {
  return plantsFor(def).plants;
}

/** Which plant tile `index` of an asteroid grew as part of (whatever is there now), or null. */
export function plantAt(def: AsteroidDef, index: number): number | null {
  return plantsFor(def).byTile.get(index) ?? null;
}
